/**
 * Life4Earth V2 — Dark Theme (Default)
 *
 * Maps CSS custom property names → values.
 * These values are the CURRENT production colors captured from
 * App.css, LeftNav.css, and component inline styles.
 */
import { SLATE, BLUE, RED, GREEN, AMBER, PURPLE } from '../tokens/colors';
import type { ThemeTokens } from '../types';

export const DARK_THEME: ThemeTokens = {
  /* ---- Backgrounds ---- */
  '--bg-primary':   SLATE[950],               // #020617
  '--bg-secondary': 'rgba(15, 23, 42, 0.85)',  // SLATE[900] at 85% — lets bg texture show through
  '--bg-surface':   'rgba(255, 255, 255, 0.04)', // neutral surface — no blue tint
  '--bg-tertiary':  SLATE[800],               // #1e293b
  '--bg-elevated':  'rgba(15, 23, 42, 0.9)',  // frosted overlay
  '--bg-secondary-header': '#0b1222',         // darkened --bg-secondary for section headers
  '--bg-primary-translucent': 'rgba(2, 6, 23, 0.92)', // --bg-primary at 92% — lets bg texture through

  /* ---- Text ---- */
  '--text-primary':   SLATE[50],   // #f8fafc
  '--text-secondary': SLATE[200],  // #e2e8f0
  '--text-tertiary':  SLATE[400],  // #94a3b8
  '--text-muted':     SLATE[500],  // #64748b

  /* ---- Accent / Interactive ---- */
  '--accent-primary':       BLUE[500],   // #3b82f6
  '--accent-primary-hover': BLUE[600],   // #2563eb
  '--accent-secondary':     PURPLE[500], // #a855f7

  /* ---- Secondary action (amber/gold) ---- */
  '--action-secondary':       AMBER[600],  // #d97706
  '--action-secondary-hover': AMBER[700],  // #b45309
  '--action-secondary-bg':    'rgba(217, 119, 6, 0.12)',

  /* ---- Semantic status ---- */
  '--color-success': GREEN[500],  // #22c55e
  '--color-warning': AMBER[500],  // #f59e0b
  '--color-error':   RED[500],    // #ef4444

  /* ---- Pillar brand colors ---- */
  '--pillar-problems':    RED[500],     // #ef4444
  '--pillar-solutions':   GREEN[500],   // #22c55e
  '--pillar-enablers':    BLUE[500],    // #3b82f6
  '--pillar-communities': SLATE[200],   // #e2e8f0
  '--pillar-projects':    SLATE[400],   // #94a3b8 — dark grey

  /* ---- Content bookshelf spine colors (§5.1 — Proposed palette) ---- */
  '--content-spine-tracker':   '#06b6d4',   // cyan-500
  // Blog (internal key: journal_entry). Teal, not amber — amber is --color-warning.
  '--content-spine-journal':   '#14b8a6',   // teal-500
  '--content-spine-howto':     '#ea580c',   // orange-600
  '--content-spine-mini':      '#84cc16',   // lime-500
  '--content-spine-chronicle': '#6366f1',   // indigo-500
  '--content-spine-lesson':    '#f43f5e',   // rose-500
  '--content-spine-practice':  '#8b5cf6',   // violet-500
  '--content-spine-news':      '#d946ef',   // fuchsia-500
  '--content-spine-scholarly': '#78716c',   // stone-500

  /* ---- Interactive surfaces ---- */
  '--bg-hover':          'rgba(255, 255, 255, 0.04)',
  '--bg-hover-strong':   'rgba(255, 255, 255, 0.07)',
  '--bg-active':         'rgba(255, 255, 255, 0.10)',
  '--accent-bg':         'rgba(59, 130, 246, 0.16)',
  '--accent-bg-40':      'rgba(59, 130, 246, 0.25)',
  '--accent-border':     'rgba(59, 130, 246, 0.35)',
  '--accent-text':       '#93c5fd',
  '--accent-secondary-bg':      'rgba(168, 85, 247, 0.12)',
  '--accent-secondary-bg-soft': 'rgba(168, 85, 247, 0.06)',
  '--accent-secondary-border':  'rgba(168, 85, 247, 0.25)',
  '--info-bg':           'rgba(59, 130, 246, 0.12)',
  '--info-border':       'rgba(59, 130, 246, 0.25)',
  '--success-bg':        'rgba(34, 197, 94, 0.16)',
  '--success-bg-strong': 'rgba(34, 197, 94, 0.22)',
  '--success-border':    'rgba(34, 197, 94, 0.25)',
  '--text-on-accent':    '#ffffff',

  /* ---- Borders ---- */
  '--border-subtle':  'rgba(148, 163, 184, 0.14)',
  '--border-default': 'rgba(148, 163, 184, 0.2)',
  '--border-strong':  'rgba(148, 163, 184, 0.4)',

  /* ---- Cards ----
   * DEV-843: --card-bg is SOLID. Translucent fills compose differently against
   * different parents (page bg vs panel bg vs background image), causing the
   * same component to render at different perceived shades depending on where
   * it's mounted. Solid fills render identically everywhere. The border can
   * stay alpha — it's a 1px line, the compose effect is invisible at that scale.
   *
   * Custom themes set their own solid via the theme designer. The principle
   * (chrome solid, interactive states translucent) applies to every theme. */
  '--card-bg':     '#1e293b',
  '--card-border': 'rgba(148, 163, 184, 0.12)',

  /* ---- Chart canvas ----
   * DEV-845: Canonical "data well" surface inside a card. Deliberately
   * darker than --card-bg in dark theme so charts read as a recessed zone
   * within their parent card (Datadog/Grafana/Tableau pattern). Custom
   * themes can override via the theme designer. */
  '--chart-canvas-bg': '#161f30',

  /* ---- Card scrims & overlays ---- */
  '--card-scrim':       'rgba(0, 0, 0, 0.65)',   // bottom text bar on image cards
  '--card-overlay':     'rgba(0, 0, 0, 0.75)',   // hover overlay revealing description
  '--card-scrim-blur':  '8px',                    // backdrop blur for scrims

  /* ---- Status text colors ---- */
  '--success-text':   '#4ade80',
  '--info-text':      '#60a5fa',
  '--warning-text':   '#fbbf24',
  '--error-text':     '#f87171',

  /* ---- Error status ---- */
  '--error-bg':     'rgba(239, 68, 68, 0.12)',
  '--error-border': 'rgba(239, 68, 68, 0.25)',
  '--warning-bg':   'rgba(245, 158, 11, 0.12)',
  '--warning-border': 'rgba(245, 158, 11, 0.25)',

  /* ---- Overlay / Scrim ---- */
  '--bg-scrim': 'rgba(0, 0, 0, 0.6)',

  /* ---- Shadows ---- */
  '--shadow-sm':   '0 1px 2px 0 rgba(0, 0, 0, 0.2)',
  '--shadow-base': '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
  '--shadow-lg':   '0 20px 25px -5px rgba(0, 0, 0, 0.4)',

  /* ---- Transitions ---- */
  '--duration-fast': '120ms',
  '--duration-base': '150ms',
  '--duration-slow': '200ms',
  '--easing-out':    'cubic-bezier(0, 0, 0.2, 1)',

  /* ---- Left Navigation ---- */
  '--leftnav-shadow':         '4px 0 12px rgba(0, 0, 0, 0.25)',
  '--leftnav-bg':             SLATE[950],                  // #020617 — matches --bg-primary by default
  '--leftnav-border':         'rgba(148, 163, 184, 0.14)', // matches --border-subtle
  '--leftnav-item-text':      SLATE[200],                  // #e2e8f0 — matches --text-secondary
  '--leftnav-item-hover':     'rgba(255, 255, 255, 0.07)', // matches --bg-hover-strong
  '--leftnav-item-active-bg': 'rgba(59, 130, 246, 0.16)',  // matches --accent-bg
  '--leftnav-item-active-border': 'rgba(59, 130, 246, 0.35)', // matches --accent-border
  '--leftnav-item-active-text': SLATE[50],                 // #f8fafc — matches --text-primary
  '--leftnav-group-label':    SLATE[500],                  // #64748b — matches --text-muted

  /* ---- Rail (POLICY_NAVIGATION_RAILS.md) ----
   * Mirrors --leftnav-* but one slate step lighter (SLATE[900] vs [950])
   * so LeftNav and the Rail read as two cohesive-but-distinct columns.
   * Backgrounds are solid (no alpha) — the Rail must stay opaque over
   * dense pages per the overlay rule. */
  '--rail-width-switcher':    '220px',
  '--rail-width-list':        '340px',
  '--rail-width-collapsed':   '56px',
  '--rail-breakpoint':        '960px',
  '--rail-shadow':            '4px 0 12px rgba(0, 0, 0, 0.18)',
  '--rail-bg':                SLATE[900],                  // #0f172a — one step up from --leftnav-bg
  '--rail-border':            'rgba(148, 163, 184, 0.14)', // matches --border-subtle
  '--rail-item-text':         SLATE[200],                  // #e2e8f0 — matches --text-secondary
  '--rail-item-muted':        SLATE[400],                  // #94a3b8 — matches --text-muted-strong
  '--rail-item-hover':        'rgba(255, 255, 255, 0.06)',
  '--rail-item-active-bg':    'rgba(59, 130, 246, 0.16)',  // matches --accent-bg
  '--rail-item-active-border':'rgba(59, 130, 246, 0.35)',  // matches --accent-border
  '--rail-item-active-text':  SLATE[50],                   // #f8fafc — matches --text-primary
  '--rail-header-label':      SLATE[500],                  // #64748b — matches --text-muted

  /* ---- Topbar specific ---- */
  '--topbar-shadow':  '0 4px 12px rgba(0, 0, 0, 0.25)',
  '--topbar-bg':     'rgba(2, 6, 23, 0.6)',
  '--topbar-border': 'rgba(148, 163, 184, 0.14)',

  /* ---- Header glass effect ---- */
  '--header-glass-bg':  'rgba(15, 23, 42, 0.82)',
  '--header-glass-tab': 'rgba(15, 23, 42, 0.82)',

  /* ---- Scrollbar ---- */
  '--scrollbar-track': SLATE[900],
  '--scrollbar-thumb': SLATE[600],

  /* ---- Impact Signature: Dimension colors ---- */
  '--dim-mind':  '#7ea3cc',
  '--dim-voice': '#5aabb5',
  '--dim-heart': '#c77a6f',
  '--dim-body':  '#6aab7a',
  '--dim-wings': '#c9a24e',
  '--dim-roots': '#8B7355',

  /* ---- Impact Signature: Medal tier colors ---- */
  '--medal-bronze':   '#CD7F32',
  '--medal-silver':   '#B0B8C4',
  '--medal-gold':     '#D4A843',
  '--medal-platinum': '#C8C6C2',

  /* ---- Globe / Discover Shader ---- */
  '--globe-bg-dark':   '#020a1a',     // outer edge of radial gradient
  '--globe-bg-mid':    '#041247',     // center glow color
  '--globe-star-color':'#ebeeff',     // star tint (near-white, slight blue)
  '--globe-glow':      '#4df0ff',     // fresnel rim glow / accent highlights
};
